/**
 * 慣性スクロール（Lenis）
 *
 * @description ホイールの移動をネイティブのスクロール位置へ慣性補間で流し込む。位置そのものは
 *              ネイティブのままなので、sticky もscroll連動（ヘッダーの出し入れ・ScrollTrigger）も
 *              手を入れずに追従する。
 *
 *              PC（1025px以上）でモーションを抑える設定でないときだけ動かす。タッチ端末は
 *              syncTouch: false でネイティブに任せる（iOSの慣性を殺さないため）。
 *
 *              参照: https://github.com/darkroomengineering/lenis
 */

import Lenis from 'lenis';

/** 慣性の強さ。小さいほど長く滑る */
const LERP = 0.1;

/** この幅以下ではネイティブのスクロールに任せる */
const SP = '(max-width: 1024px)';

/** 動いているインスタンス。条件を満たさない環境では null のまま */
let lenis: Lenis | null = null;

/**
 * 動いているLenisを返す
 *
 * @returns インスタンス。動いていなければ null
 */
const getLenis = (): Lenis | null => lenis;

/**
 * 慣性を止める
 *
 * @description 画面の切り替え中とスプラッシュ中に使う。どちらもページが動いてはいけない場面
 * @returns {void}
 */
const stopSmoothScroll = (): void => {
    lenis?.stop();
};

/**
 * 慣性を再開する
 *
 * @returns {void}
 */
const startSmoothScroll = (): void => {
    lenis?.start();
};

/**
 * いまのスクロール位置をLenisに教え直す
 *
 * @description 非同期遷移で中身ごと入れ替えたあとに呼ぶ。ネイティブ側だけ動かすとLenisが
 *              持っている目標値が古いままで、次のホイールで元の位置へ引き戻される
 * @returns {void}
 */
const settleSmoothScroll = (): void => {
    if (!lenis) return;

    lenis.resize();
    lenis.scrollTo(window.scrollY, { immediate: true, force: true });
};

/**
 * ページ内アンカーを慣性で送る
 *
 * @description クリックを横取りするのでURLのハッシュは自分で書き換える。既定の動作に任せると
 *              ネイティブのスクロールが先に走ってLenisと二重になる
 * @returns {void}
 */
const bindAnchors = (): void => {
    document.addEventListener('click', (e) => {
        // 止めているあいだ（スプラッシュ中・遷移中）は横取りしない。
        // 奪ったうえで動かさないと、リンクが死んだように見える
        if (!lenis || lenis.isStopped) return;

        const link = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href*="#"]');

        if (!link || link.target === '_blank') return;

        const url = new URL(link.href, location.href);

        if (url.pathname !== location.pathname || !url.hash) return;

        const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));

        if (!target) return;

        e.preventDefault();
        // 着地位置の余白は Lenis が scroll-margin-top を読んで付ける
        lenis.scrollTo(target);
        history.pushState(null, '', url.hash);
    });
};

/**
 * スプラッシュが引けるまで慣性を止めておく
 *
 * @description スプラッシュはwheelをpreventDefaultして止めているが、それはネイティブの
 *              スクロールにしか効かない。Lenisは自分でwheelを拾うので別に止める必要がある
 * @returns {void}
 */
const waitForSplash = (): void => {
    if (!document.body.classList.contains('splash-is-on')) return;

    lenis?.stop();

    const observer = new MutationObserver(() => {
        if (document.body.classList.contains('splash-is-on')) return;

        lenis?.start();
        observer.disconnect();
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
};

/**
 * 慣性スクロールを起動する
 *
 * @returns {void}
 */
const initSmoothScroll = (): void => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSP = window.matchMedia(SP).matches;

    if (reduce || isSP) return;

    lenis = new Lenis({
        lerp: LERP,
        smoothWheel: true,
        syncTouch: false,
        // 既定ではホイールを全部ページ送りに使うので、同意書のような中で
        // スクロールする枠が動かせなくなる。枠の中に行き先が残っているあいだは
        // そちらへ譲り、端まで行ったらページ側へ引き継ぐ
        allowNestedScroll: true,
    });

    /**
     * 毎フレームLenisを進める
     *
     * @param time rAFのタイムスタンプ
     * @returns {void}
     */
    const raf = (time: number): void => {
        lenis?.raf(time);
        requestAnimationFrame(raf);
    };

    requestAnimationFrame(raf);

    bindAnchors();
    waitForSplash();
};

export { getLenis, stopSmoothScroll, startSmoothScroll, settleSmoothScroll };

initSmoothScroll();
